import Close from "@mui/icons-material/Close";
import Menu from "@mui/icons-material/Menu";
import MenuOpen from "@mui/icons-material/MenuOpen";
import { Box, IconButton, Stack } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import { alpha, styled, SxProps } from "@mui/material/styles";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenantId } from "hooks/useTenant";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";

import EncampLogo from "../../assets/EncampLogo.svg";
import {
  type GroupChildNavItem,
  type GroupNavItem,
  NavItemV2,
  type StandardNavItem,
} from "./NavItemV2";
import { SectionNavRow } from "./SectionNavRow";

export type NavSection = {
  title: string;
  longTitle?: string;
  textColor?: string;
  sx?: SxProps;
  items: NavItem[];
};

export type NavItem = StandardNavItem | GroupChildNavItem | GroupNavItem;

interface NavDrawerProps {
  navSections: NavSection[];
  collapsed: boolean;
  expandedWidth: string;
  collapsedWidth: string;
  open: boolean;
  /** True when the nav should behave like a mobile overlay drawer instead of a desktop sidebar. */
  isMobileDrawerMode: boolean;
  isMobile: boolean;
  onClose: () => void;
  onNavToggle: () => void;
}

// Use stable keys so each nav row keeps the correct persisted expansion state.
function getNavItemKey(navItem: NavItem, idx: number) {
  switch (navItem.kind) {
    case "Group":
      return `group-${navItem.text}-${navItem.path}`;
    case "Standard":
    case "GroupChild":
      return `${navItem.kind}-${navItem.text}-${navItem.path ?? idx}`;
  }
}

function getSectionKey(section: NavSection, idx: number) {
  return `section-${section.title}-${idx}`;
}

const DrawerScrollArea = styled(Box)(({ theme }) => {
  const thumbBase =
    theme.palette.mode === "light" ? "#878D99" : alpha(theme.palette.text.primary, 0.38);
  const thumbHover =
    theme.palette.mode === "light" ? "#A0A6B2" : alpha(theme.palette.text.primary, 0.52);
  const trackBase = "transparent";
  const scrollbarWidth = 10;

  return {
    width: "100%",
    height: "100%",
    minHeight: 0,
    position: "relative",
    overflowY: "auto",
    backgroundColor: theme.palette.mode === "light" ? "#051E3C" : theme.palette.background.default,
    // Reserve gutter space where supported so the layout does not jump when the scrollbar is revealed.
    scrollbarGutter: "stable",
    // Keep the gutter reserved and change colors on hover instead of changing scrollbar width.
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    // WebKit scrollbar selectors remain non-standard, so treat them as a progressive enhancement.
    "&:hover": {
      scrollbarWidth: "thin",
      scrollbarColor: `${thumbBase} ${trackBase}`,
    },

    "&::-webkit-scrollbar": {
      width: scrollbarWidth,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      borderRadius: 999,
      // Keep the thumb visually inset instead of filling the entire track width.
      border: "2px solid transparent",
      backgroundClip: "padding-box",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      backgroundColor: thumbBase,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: trackBase,
      borderRadius: 999,
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "transparent",
    },
  };
});

export function LeftNavDrawerV2({
  navSections,
  collapsed,
  expandedWidth,
  collapsedWidth,
  open,
  isMobileDrawerMode,
  isMobile,
  onClose,
  onNavToggle,
}: NavDrawerProps) {
  const isCollapsed = isMobileDrawerMode ? false : collapsed;
  const drawerWidth = isMobileDrawerMode
    ? isMobile
      ? "100vw"
      : expandedWidth
    : isCollapsed
      ? collapsedWidth
      : expandedWidth;
  const navToggleLabel = isCollapsed ? "Expand navigation" : "Collapse navigation";
  const drawerVariant = isMobileDrawerMode ? "temporary" : "permanent";
  const closeOnNavigate = isMobileDrawerMode ? onClose : undefined;

  return (
    <Drawer
      variant={drawerVariant}
      open={isMobileDrawerMode ? open : true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          overflowX: "hidden",
          boxSizing: "border-box",
          borderRight: "none",
          transition: (theme) =>
            theme.transitions.create(["width"], {
              easing: isCollapsed
                ? theme.transitions.easing.sharp
                : theme.transitions.easing.easeOut,
              duration: isCollapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <DrawerScrollArea
        data-testid="left-nav-drawer"
        data-nav-mode={drawerVariant}
        data-drawer-width={drawerWidth}
        sx={{
          width: drawerWidth,
        }}
      >
        <Box component="nav" aria-label="Primary navigation" sx={{ height: "100%" }}>
          {isMobileDrawerMode && isMobile && (
            <IconButton
              aria-label="Close navigation"
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                color: "white",
              }}
            >
              <Close />
            </IconButton>
          )}
          <Stack
            sx={{
              height: isCollapsed ? "112px" : "176px",
              marginY: isCollapsed ? 1 : 0,
              transition: (theme) =>
                theme.transitions.create(["height", "margin"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "auto",
                marginBottom: "auto",
              }}
            >
              <NavLinkToRoot>
                <img
                  src={EncampLogo}
                  alt="Encamp logo"
                  style={{
                    width: isCollapsed ? "28px" : "80px",
                    transition: "width 0.1s cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                />
              </NavLinkToRoot>
            </Box>
            {!isMobileDrawerMode && (
              <IconButton
                aria-label={navToggleLabel}
                onClick={onNavToggle}
                sx={{
                  color: "white",
                  width: isCollapsed ? 40 : 44,
                  height: isCollapsed ? 40 : 44,
                  marginX: "auto",
                  marginBottom: isCollapsed ? 0 : 1,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                  },
                }}
              >
                {isCollapsed ? (
                  <Menu sx={{ fontSize: "1.375rem" }} />
                ) : (
                  <MenuOpen sx={{ fontSize: "1.375rem" }} />
                )}
              </IconButton>
            )}
          </Stack>
          <List sx={{ paddingTop: 0 }}>
            {navSections.map((section, idx) => (
              <Fragment key={getSectionKey(section, idx)}>
                <SectionNavRow section={section} collapsed={isCollapsed} />
                {section.items.map((item, idx) => (
                  <NavItemV2
                    key={getNavItemKey(item, idx)}
                    navItem={item}
                    collapsed={isCollapsed}
                    onNavigate={closeOnNavigate}
                  />
                ))}
              </Fragment>
            ))}
          </List>
        </Box>
      </DrawerScrollArea>
    </Drawer>
  );
}

const NavLinkToRoot = ({ children }: { children: React.ReactNode }) => {
  const { isStaff } = useCurrentUser();
  const tenantId = useTenantId();

  if (tenantId) {
    return <NavLink to={`/o/${tenantId}`}>{children}</NavLink>;
  }

  if (isStaff) {
    return <NavLink to="/staff">{children}</NavLink>;
  }

  return <NavLink to="/">{children}</NavLink>;
};
